import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, MessageSquare, Calendar, Send, ChevronUp, ChevronLeft, ChevronRight,
  RefreshCw, Pencil, X, Check, Plus, Trash2, AlertCircle, RefreshCcw,
  Globe, UserCheck, Upload, Eye, EyeOff, Tag as TagIcon, Paperclip,
  CheckCheck, Undo2, Lock, Phone, MousePointerClick, Mail,
} from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useBlocker, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { FilePreviewCard } from '@/components/files/FilePreviewCard'
import { FileUploadModal } from '@/components/files/FileUploadModal'
import { PhoneFieldInput } from '@/components/forms/PhoneFieldInput'
import { CategorizedTagOptions } from '@/components/tags/CategorizedTagOptions'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DateInput } from '@/components/ui/date-input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { CallButton } from '@/features/calling/components/CallButton'
import { RecordCallsPanel } from '@/features/calling/components/RecordCallsPanel'
import { RecordGroupBreadcrumbs } from '@/features/record-groups/components/RecordGroupDetails/RecordGroupBreadcrumbs'
import { EmailMessageFieldInput } from '@/features/records/components/EmailMessageFieldInput'
import { FileFieldInput } from '@/features/records/components/FileFieldInput'
import { FieldHelpText, LayoutFieldView } from '@/features/records/components/LayoutFieldView'
import { RecordButtonField } from '@/features/records/components/RecordButtonField'
import { RatingInput } from '@/features/records/components/RecordFieldInput'
import { isButtonField } from '@/features/records/utils/buttonField'
import { FIELD_VIEW_ONLY_HINT } from '@/features/records/utils/fieldAccess'
import { isFieldVisible, valuesByKey } from '@/features/records/utils/fieldConditions'
import { isWideField } from '@/features/records/utils/fieldLayout'
import { fieldWidth, isLayoutField } from '@/features/records/utils/layoutField'
import { useActivity } from '@/hooks/useActivity'
import { useCallingMe } from '@/hooks/useCalling'
import { useFileUploadConfig } from '@/hooks/useFileUpload'
import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import { useAllowedActions } from '@/hooks/usePermission'
import { useRecordTags } from '@/hooks/useRecordTags'
import { useRecordDetail } from '@/hooks/useRecords'
import { useTagCategories } from '@/hooks/useTagCategories'
import { useTags } from '@/hooks/useTags'
import { useMarkRecordFollowUpComplete, useReopenRecordFollowUp } from '@/hooks/useTasks'
import { uploadRecordValueFile } from '@/services/api/file.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { Activity, AddActivityRequest } from '@/types/api/activity'
import { ActivityActionType, RecordSourceType, type FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { formatDate, formatDateTime, getRelativeTime, parseDate, toISODateString } from '@/utils/date'
import { parseApiError } from '@/utils/error-parser'
import { formatFieldValue, getFieldInputType, validateFieldValue } from '@/utils/field-formatters'
import { displayActivityValue } from '@/utils/file-utils'

// ─── Field type helpers ───────────────────────────────────────────────────────

const TYPE_META: Record<string, { icon: string; cls: string }> = {
  EMAIL:    { icon: '@',  cls: 'bg-sky-50 text-sky-600' },
  DATE:     { icon: '◷',  cls: 'bg-violet-50 text-violet-600' },
  NUMBER:   { icon: '#',  cls: 'bg-orange-50 text-orange-600' },
  CURRENCY: { icon: '$',  cls: 'bg-emerald-50 text-emerald-600' },
  SELECT:   { icon: '≡',  cls: 'bg-pink-50 text-pink-600' },
  TEXT:     { icon: 'T',  cls: 'bg-neutral-100 text-neutral-500' },
  FILE_UPLOAD: { icon: '↑', cls: 'bg-indigo-50 text-indigo-600' },
  RADIO: { icon: '◉', cls: 'bg-blue-50 text-blue-600' },
  CHECKBOX: { icon: '☑', cls: 'bg-teal-50 text-teal-600' },
  RANGE: { icon: '↔', cls: 'bg-orange-50 text-orange-600' },
  COLOR: { icon: '●', cls: 'bg-fuchsia-50 text-fuchsia-600' },
  URL: { icon: '↗', cls: 'bg-sky-50 text-sky-600' },
  TOGGLE: { icon: '◐', cls: 'bg-lime-50 text-lime-600' },
  PHONE: { icon: '☎', cls: 'bg-purple-50 text-purple-600' },
  PARAGRAPH: { icon: '¶', cls: 'bg-primary-50 text-primary-600' },
  DATETIME: { icon: '▦', cls: 'bg-rose-50 text-rose-600' },
  TIME: { icon: '◷', cls: 'bg-rose-50 text-rose-600' },
  RATING: { icon: '★', cls: 'bg-amber-50 text-amber-600' },
  PERCENT: { icon: '%', cls: 'bg-orange-50 text-orange-600' },
  EMAIL_MESSAGE: { icon: '✉', cls: 'bg-teal-50 text-teal-600' },
}
const typeMeta = (t: string) => TYPE_META[t] ?? TYPE_META.TEXT

// ─── Tag chip ─────────────────────────────────────────────────────────────────

function TagChip({ label, colorHex, onRemove }: {
  label: string; colorHex?: string | null; onRemove?: () => void
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border"
      style={colorHex
        ? { backgroundColor: colorHex + '22', borderColor: colorHex + '55', color: colorHex }
        : undefined}
      // fallback when no colour
      data-no-color={!colorHex ? '' : undefined}
    >
      {!colorHex && <TagIcon className="h-2.5 w-2.5 text-neutral-400 shrink-0" />}
      {colorHex && (
        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: colorHex }} />
      )}
      {label}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="ml-0.5 opacity-50 hover:opacity-100 transition-opacity"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  )
}

// ─── CreatorCard ──────────────────────────────────────────────────────────────

function CreatorCard({ sourceType, creatorName, creatorEmail }: {
  sourceType?: string; creatorName?: string | null; creatorEmail?: string | null
}) {
  if (!creatorName && !creatorEmail) return null

  const SOURCE_META = {
    [RecordSourceType.INTERNAL_USER]: { label: 'Internal user', Icon: UserCheck, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    [RecordSourceType.PUBLIC_FORM]:   { label: 'Public form',   Icon: Globe,      cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    [RecordSourceType.CSV_IMPORT]:    { label: 'CSV import',    Icon: Upload,     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  }
  const meta = sourceType ? SOURCE_META[sourceType as RecordSourceType] : SOURCE_META[RecordSourceType.INTERNAL_USER]
  const { label, Icon, cls } = meta ?? SOURCE_META[RecordSourceType.INTERNAL_USER]

  const initials = creatorName
    ?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) ?? '?'

  return (
    <div className="shrink-0 mx-4 mb-4 rounded-xl border border-neutral-100 bg-neutral-50/60 p-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-[11px] font-bold text-white shrink-0">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-neutral-800 truncate">{creatorName ?? '—'}</p>
        {creatorEmail && (
          <p className="text-[11px] text-neutral-400 truncate">{creatorEmail}</p>
        )}
      </div>
      <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold shrink-0', cls)}>
        <Icon className="h-2.5 w-2.5" />{label}
      </span>
    </div>
  )
}

// ─── Client-side validation (mirrors backend FormFieldValidationService) ──────

interface FieldMeta {
  type: string
  required: boolean
  validations: Record<string, unknown> | null
  options: Record<string, unknown> | null
}

function validateLocally(value: string, field: FieldMeta): string | null {
  const shared = validateFieldValue(value, field.type as FormFieldType, field.required, field.validations, field.options)
  if (shared) return shared
  const trimmed = value.trim()
  if (field.required && !trimmed) return 'This field is required'
  if (!trimmed) return null
  const v = field.validations ?? {}
  const o = field.options ?? {}
  switch (field.type) {
    case 'TEXT': {
      const min = v.minLength as number | undefined
      const max = v.maxLength as number | undefined
      if (min != null && trimmed.length < min) return `Must be at least ${min} characters`
      if (max != null && trimmed.length > max) return `Must be at most ${max} characters`
      break
    }
    case 'EMAIL': {
      const pattern = v.pattern as string | undefined
      const re = pattern ? new RegExp(pattern) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!re.test(trimmed)) return 'Invalid email format'
      break
    }
    case 'NUMBER':
    case 'CURRENCY': {
      const num = parseFloat(trimmed)
      if (isNaN(num)) return 'Must be a valid number'
      const min = v.min as number | undefined
      const max = v.max as number | undefined
      if (min != null && num < min) return `Must be ≥ ${min}`
      if (max != null && num > max) return `Must be ≤ ${max}`
      break
    }
    case 'DATE':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return 'Must be a valid date (YYYY-MM-DD)'
      break
    case 'SELECT': {
      const values = (o.values as string[] | undefined) ?? []
      if (values.length && !values.includes(trimmed)) return `Must be one of: ${values.join(', ')}`
      break
    }
  }
  return null
}

// ─── FieldCell — single editable field ───────────────────────────────────────

interface FieldCellProps {
  fieldId: string
  fieldKey: string
  fieldLabel: string
  fieldType: string
  required: boolean
  validations: Record<string, unknown> | null
  options: Record<string, unknown> | null
  value: string
  pendingValue?: string
  saveError?: string
  canEdit: boolean
  /** Field access makes this field read-only for the user even though they may edit the record. */
  viewOnly?: boolean
  showFieldKeys: boolean    // Phase 3 — toggled via the Eye button in the panel header
  recordGroupId: string
  recordId: string
  /** The record's values by field key, to fill an email field's default search. */
  valuesByKey?: Record<string, string>
  /** Called on every change; {@code error} is the local validation message, if any. */
  onChange: (val: string, error?: string | null) => void
}

export function FieldCell({
  fieldId, fieldKey, fieldLabel, fieldType, required, validations, options,
  value, pendingValue, saveError, canEdit, viewOnly = false, showFieldKeys, recordGroupId, recordId, valuesByKey, onChange,
}: FieldCellProps) {
  const [editing, setEditing]     = useState(false)
  const [draft, setDraft]         = useState('')
  const [liveError, setLiveError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // These fields bring their own controls, so the cell never switches into edit mode
  const hasOwnControls = fieldType === 'FILE_UPLOAD' || fieldType === 'EMAIL_MESSAGE'
  const { data: fileConfig } = useFileUploadConfig()

  const isDirty    = pendingValue !== undefined
  const displayVal = isDirty ? pendingValue : value
  const meta       = typeMeta(fieldType)
  const chipError  = editing ? liveError : saveError
  const fieldMeta: FieldMeta = { type: fieldType, required, validations, options }

  const startEdit = useCallback(() => {
    if (!canEdit) return
    setDraft(displayVal ?? '')
    setLiveError(null)
    setEditing(true)
  }, [canEdit, displayVal])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  // Edits go straight into the page's pending changes; "Update Data" saves them together.
  const handleDraftChange = (val: string) => {
    const err = validateLocally(val, fieldMeta)
    setDraft(val)
    setLiveError(err)
    onChange(val, err)
  }

  const finish = () => { setEditing(false); setLiveError(null) }

  // Escape (handled on the cell, so every editor gets it) puts the saved value back
  const cancel = () => { finish(); setDraft(''); onChange(value, null) }

  // Enter adds a line in a paragraph, so it closes the editor with Ctrl/Cmd+Enter
  const onTextAreaKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); finish() }
  }

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); finish() }
  }

  // A click anywhere else closes the editor (the value is already kept); popups such as pickers are portalled.
  useEffect(() => {
    if (!editing) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (rootRef.current?.contains(target) || target?.closest('[data-radix-popper-content-wrapper],[role="dialog"]')) return
      finish()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [editing])

  const hasError = !!chipError
  const width = fieldWidth({ options })
  // The designer's width wins; a paragraph always takes the whole row
  const spansRow = width ? width === 'full' : fieldType === 'PARAGRAPH' || isWideField(fieldType, displayVal, options)
  const choiceValues = (((options?.choices ?? options?.values) as Array<string | { value: string }> | undefined) ?? [])
    .map(choice => typeof choice === 'string' ? choice : choice.value)
  const selectedChoices = (() => { try { const parsed = JSON.parse(draft); return Array.isArray(parsed) ? parsed : [] } catch { return [] } })()

  return (
    <div
      ref={rootRef}
      onKeyDown={(e) => { if (editing && e.key === 'Escape') cancel() }}
      className={cn(
        'group relative rounded-xl border transition-all',
        spansRow && 'sm:col-span-2',
        hasError && !editing
          ? 'border-rose-200 bg-rose-50/40'
          : isDirty
            ? 'border-amber-200 bg-amber-50/60'
            : 'border-neutral-300 bg-neutral-50/40 hover:border-primary-300 hover:bg-white',
        canEdit && !editing && !hasOwnControls && 'cursor-pointer',
      )}
      onClick={() => !editing && !hasOwnControls && startEdit()}
    >
      <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 flex-wrap">
        <span className={cn('inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold select-none shrink-0', meta.cls)}>
          {meta.icon}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 truncate flex-1 min-w-0">
          {fieldLabel}
        </span>
        {/* Phase 3 — only render when toggle is on */}
        {showFieldKeys && (
          <span
            className="font-mono text-[10px] text-neutral-400 bg-neutral-100 border border-neutral-200 rounded px-1.5 py-px leading-none shrink-0 select-all"
            title="Use this key in automation templates, e.g. {{fieldKey}}"
          >
            {`{{${fieldKey}}}`}
          </span>
        )}
        {required && <span className="text-rose-400 text-[10px] leading-none">*</span>}
        {viewOnly && (
          <span title={FIELD_VIEW_ONLY_HINT} aria-label={FIELD_VIEW_ONLY_HINT} className="text-neutral-400">
            <Lock className="h-3 w-3" />
          </span>
        )}
        {isDirty && !hasError && (
          <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 bg-amber-100 rounded px-1 py-px">edited</span>
        )}
      </div>

      <div className="px-3 pb-3 min-h-[28px]">
        {fieldType === 'EMAIL_MESSAGE' ? (
          <EmailMessageFieldInput
            id={fieldId}
            field={{ options }}
            value={displayVal ?? ''}
            onChange={(val) => onChange(val, null)}
            disabled={!canEdit}
            valuesByKey={valuesByKey}
          />
        ) : fieldType === 'FILE_UPLOAD' ? (
          <FileFieldInput
            field={{ id: fieldId, label: fieldLabel, options, validations }}
            value={displayVal ?? ''}
            onChange={onChange}
            upload={(file, opts) => uploadRecordValueFile(recordGroupId, fieldId, file, opts)}
            fileConfig={fileConfig}
            canEdit={canEdit}
            error={chipError}
            compact
          />
        ) : editing ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              {fieldType === 'SELECT' ? <select autoFocus value={draft} onChange={e => handleDraftChange(e.target.value)} className={cn('flex-1 min-w-0 rounded-lg border bg-white px-2.5 py-1.5 text-sm font-medium text-neutral-900 focus:outline-none focus:ring-2 focus:border-transparent', liveError ? 'border-rose-300 focus:ring-rose-400' : 'border-primary-300 focus:ring-primary-400')}><option value="" disabled={required}>Select {fieldLabel}…</option>{choiceValues.map(choice => <option key={choice} value={choice}>{choice}</option>)}</select> : fieldType === 'RADIO' ? <div className="flex-1 space-y-1">{choiceValues.map(choice => <label key={choice} className="flex items-center gap-2 text-xs"><input type="radio" name={fieldId} checked={draft === choice} onChange={() => handleDraftChange(choice)} />{choice}</label>)}</div> : fieldType === 'CHECKBOX' ? (String(options?.selectionMode ?? 'MULTIPLE').toUpperCase() === 'SINGLE' ? <div className="flex-1 space-y-1">{choiceValues.map(choice => <label key={choice} className="flex items-center gap-2 text-xs"><input type="radio" name={fieldId} checked={draft === choice} onChange={() => handleDraftChange(choice)} />{choice}</label>)}</div> : <div className="flex-1 space-y-1">{choiceValues.map(choice => <label key={choice} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selectedChoices.includes(choice)} onChange={e => handleDraftChange(JSON.stringify(e.target.checked ? [...selectedChoices, choice] : selectedChoices.filter(v => v !== choice)))} />{choice}</label>)}</div>) : fieldType === 'PARAGRAPH' ? <textarea autoFocus rows={4} value={draft} onChange={e => handleDraftChange(e.target.value)} onKeyDown={onTextAreaKey} className={cn('flex-1 min-w-0 rounded-lg border bg-white px-2.5 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:border-transparent', liveError ? 'border-rose-300 focus:ring-rose-400' : 'border-primary-300 focus:ring-primary-400')} /> : fieldType === 'RATING' ? <div className="flex-1"><RatingInput field={{ label: fieldLabel, options }} id={fieldId} value={draft} onChange={handleDraftChange} invalid={!!liveError} /></div> : fieldType === 'TOGGLE' ? <label className="flex-1 flex items-center gap-2 text-xs"><input type="checkbox" checked={draft === 'true'} onChange={e => handleDraftChange(String(e.target.checked))} />Enabled</label> : fieldType === 'PHONE' ? <PhoneFieldInput value={draft} error={liveError} options={options} compact onChange={handleDraftChange} /> : fieldType === 'DATE' ? <DateInput autoFocus value={draft} onChange={handleDraftChange} onKeyDown={onKey} className={cn('rounded-lg px-2.5 py-1.5 text-sm font-medium', liveError ? 'border-rose-300' : 'border-primary-300')} /> : <input ref={inputRef} type={getFieldInputType(fieldType as FormFieldType)} value={draft} onChange={e => handleDraftChange(e.target.value)} onKeyDown={onKey} min={String(validations?.min ?? '')} max={String(validations?.max ?? '')} className={cn('flex-1 min-w-0 rounded-lg border bg-white px-2.5 py-1.5 text-sm font-medium text-neutral-900 focus:outline-none focus:ring-2 focus:border-transparent', liveError ? 'border-rose-300 focus:ring-rose-400' : 'border-primary-300 focus:ring-primary-400')} />}
            </div>
            {liveError && (
              <div className="flex items-center gap-1 text-[10px] font-medium text-rose-600">
                <AlertCircle className="h-2.5 w-2.5 shrink-0" />{liveError}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className={cn('text-sm font-semibold leading-snug',
                fieldType === 'PARAGRAPH' ? 'whitespace-pre-wrap break-words min-w-0' : 'truncate',
                displayVal ? 'text-neutral-800' : 'text-neutral-300 font-normal italic'
              )}>
                {displayVal ? formatFieldValue(displayVal, fieldType as FormFieldType) : 'Empty'}
              </span>
              {fieldType === 'PHONE' && !isDirty && (
                // Calls the saved number (the server reads it from the record), so not while edited.
                <CallButton recordId={recordId} phoneFieldId={fieldId} value={value} className="ml-auto" />
              )}
              {canEdit && !chipError && (
                <Pencil className="h-3 w-3 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              )}
              {chipError && <AlertCircle className="h-3 w-3 text-rose-400 shrink-0" />}
            </div>
            {chipError && <p className="text-[10px] font-medium text-rose-500 leading-snug">{chipError}</p>}
          </div>
        )}
        <FieldHelpText field={{ options }} className="mt-1" />
      </div>

    </div>
  )
}

// ─── Activity color map ────────────────────────────────────────────────────────

const ACTIVITY_META: Record<string, { group: 'note' | 'field_update' | 'automation'; cls: string }> = {
  [ActivityActionType.ADD_NOTE]:      { group: 'note',         cls: 'note' },
  [ActivityActionType.CREATE]:        { group: 'automation',   cls: 'create' },
  [ActivityActionType.UPDATE_FIELD]:  { group: 'field_update', cls: 'field_update' },
  [ActivityActionType.FILE_UPLOAD]:   { group: 'field_update', cls: 'file' },
  [ActivityActionType.DELETE]:        { group: 'automation',   cls: 'delete' },
  [ActivityActionType.COMPLETE]:      { group: 'automation',   cls: 'complete' },
  [ActivityActionType.REOPEN]:        { group: 'automation',   cls: 'reopen' },
  [ActivityActionType.CALL]:          { group: 'automation',   cls: 'call' },
  [ActivityActionType.TAG_ADDED]:     { group: 'field_update', cls: 'tag' },
  [ActivityActionType.TAG_REMOVED]:   { group: 'field_update', cls: 'tag' },
  [ActivityActionType.BUTTON_CLICKED]: { group: 'automation',  cls: 'button' },
  [ActivityActionType.EMAIL_SENT]:    { group: 'automation',   cls: 'email' },
}

function getActivityMeta(actionType: string) {
  return ACTIVITY_META[actionType] ?? { group: 'automation' as const, cls: 'default' }
}

// bubble + border color styles per activity type
const BUBBLE_STYLES: Record<string, { bubble: string; border: string }> = {
  note:         { bubble: 'bg-violet-50',   border: 'border-violet-200' },
  field_update: { bubble: 'bg-sky-50',      border: 'border-sky-200' },
  create:       { bubble: 'bg-emerald-50',  border: 'border-emerald-200' },
  delete:       { bubble: 'bg-rose-50',     border: 'border-rose-200' },
  complete:     { bubble: 'bg-teal-50',     border: 'border-teal-200' },
  reopen:       { bubble: 'bg-amber-50',    border: 'border-amber-200' },
  file:         { bubble: 'bg-indigo-50',   border: 'border-indigo-200' },
  call:         { bubble: 'bg-lime-50',     border: 'border-lime-200' },
  tag:          { bubble: 'bg-fuchsia-50',  border: 'border-fuchsia-200' },
  button:       { bubble: 'bg-sky-50',      border: 'border-sky-200' },
  email:        { bubble: 'bg-teal-50',     border: 'border-teal-200' },
  default:      { bubble: 'bg-neutral-50',  border: 'border-neutral-200' },
}

function iconFor(actionType: string): { icon: React.ReactNode; bg: string } {
  const map: Record<string, { icon: React.ReactNode; bg: string }> = {
    [ActivityActionType.CREATE]:       { icon: <Plus className="h-3 w-3" />,      bg: 'bg-emerald-500' },
    [ActivityActionType.UPDATE_FIELD]: { icon: <Pencil className="h-3 w-3" />,    bg: 'bg-sky-500' },
    [ActivityActionType.FILE_UPLOAD]:  { icon: <Upload className="h-3 w-3" />,    bg: 'bg-indigo-500' },
    [ActivityActionType.DELETE]:       { icon: <Trash2 className="h-3 w-3" />,    bg: 'bg-rose-500' },
    [ActivityActionType.COMPLETE]:     { icon: <CheckCheck className="h-3 w-3" />, bg: 'bg-teal-500' },
    [ActivityActionType.REOPEN]:       { icon: <Undo2 className="h-3 w-3" />,     bg: 'bg-amber-500' },
    [ActivityActionType.CALL]:         { icon: <Phone className="h-3 w-3" />,     bg: 'bg-lime-600' },
    [ActivityActionType.TAG_ADDED]:    { icon: <TagIcon className="h-3 w-3" />,   bg: 'bg-fuchsia-500' },
    [ActivityActionType.TAG_REMOVED]:  { icon: <TagIcon className="h-3 w-3" />,   bg: 'bg-neutral-400' },
    [ActivityActionType.BUTTON_CLICKED]: { icon: <MousePointerClick className="h-3 w-3" />, bg: 'bg-sky-500' },
    [ActivityActionType.EMAIL_SENT]:   { icon: <Mail className="h-3 w-3" />,      bg: 'bg-teal-500' },
  }
  return map[actionType] ?? { icon: <MessageSquare className="h-3 w-3" />, bg: 'bg-neutral-400' }
}

// ─── ActivityBubble ───────────────────────────────────────────────────────────

function ActivityBubble({ activity }: { activity: Activity }) {
  const meta = getActivityMeta(activity.actionType)
  const bubbleStyle = BUBBLE_STYLES[meta.cls] ?? BUBBLE_STYLES.default
  const isNote = meta.group === 'note'
  const initials = activity.actorName
    ?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) ?? '?'

  // ── Icon wrapper (absolutely positioned on the left, no per-item line) ─────
  // The continuous timeline line lives in the parent feed container (z-0).
  // This icon sits on top (z-10) with a white ring to cleanly break the line.
  const IconWrap = ({ children, className }: { children: React.ReactNode; className: string }) => (
    <div className="absolute left-0 top-0 bottom-0 w-6 flex flex-col items-center">
      <div className={cn(
        'relative z-10 mt-0.5 h-6 w-6 rounded-full flex items-center justify-center text-white',
        'ring-2 ring-white shrink-0',
        className,
      )}>{children}</div>
    </div>
  )

  if (isNote) {
    return (
      <div className="relative pl-8 pb-3">
        <IconWrap className="bg-gradient-to-br from-violet-400 to-violet-600">
          <span className="text-[9px] font-bold">{initials}</span>
        </IconWrap>
        <div className={cn('space-y-1 ml-1 p-2.5 rounded-2xl border ', bubbleStyle.bubble, bubbleStyle.border)}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-semibold text-neutral-700 truncate">{activity.actorName}</span>
            {activity.tagLabel && (
              <TagChip label={activity.tagLabel} colorHex={activity.tagColorHex} />
            )}
            {activity.followUpTagLabel && (
              <TagChip label={activity.followUpTagLabel} colorHex={activity.followUpTagColorHex} />
            )}
            <span className="text-[10px] text-neutral-400 ml-auto tabular-nums shrink-0">
              {getRelativeTime(activity.timestamp)}
            </span>
          </div>
          <div className="bg-violet-50 border border-violet-100 rounded-2xl rounded-tl-sm px-3.5 py-2.5">
            <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">{activity.note}</p>
          </div>
        </div>
      </div>
    )
  }

  const si = iconFor(activity.actionType)
  const isTagChange = activity.actionType === ActivityActionType.TAG_ADDED
    || activity.actionType === ActivityActionType.TAG_REMOVED

  const getText = () => {
    switch (activity.actionType) {
      case ActivityActionType.CREATE: return 'Record created'
      case ActivityActionType.ADD_NOTE: return 'Note added'
      case ActivityActionType.UPDATE_FIELD: {
        if (activity.redacted) return 'Updated a restricted field'
        // FILE_UPLOAD values are S3 keys — show just the file name, not the full path.
        const fmt = (v: string | null) => `"${displayActivityValue(v, activity.fieldType)}"`
        return `Updated "${activity.fieldLabel}"${activity.oldValue ? ` · was ${fmt(activity.oldValue)}` : ''}${activity.newValue ? ` → ${fmt(activity.newValue)}` : ''}`
      }
      case ActivityActionType.DELETE: return 'Record deleted'
      case ActivityActionType.FILE_UPLOAD: return 'Uploaded a file'
      case ActivityActionType.COMPLETE: return 'Follow-up completed'
      case ActivityActionType.REOPEN: return 'Follow-up reopened'
      // The call summary ("Outbound call · Answered · 3m 12s") is the whole entry.
      case ActivityActionType.CALL: return activity.note ?? 'Call'
      case ActivityActionType.TAG_ADDED: return 'Added tag'
      case ActivityActionType.TAG_REMOVED: return 'Removed tag'
      case ActivityActionType.BUTTON_CLICKED:
        return activity.redacted ? 'Clicked a restricted button' : `Clicked "${activity.fieldLabel}"`
      case ActivityActionType.EMAIL_SENT: return 'Email sent'
      default: return 'Action performed'
    }
  }

  return (
    <div className="relative pl-8 pb-3">
      <IconWrap className={si.bg}>{si.icon}</IconWrap>
      <div className={cn('min-w-0 pt-0.5 ml-1 p-2.5 rounded-2xl border ', bubbleStyle.bubble, bubbleStyle.border)}>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-neutral-600 leading-snug whitespace-normal break-words">
          {getText()}
          {isTagChange && activity.tagLabel && (
            <TagChip label={activity.tagLabel} colorHex={activity.tagColorHex} />
          )}
        </p>
        <p className="text-[10px] text-neutral-400 mt-0.5">
          {activity.actorName} · {getRelativeTime(activity.timestamp)}
        </p>
        {!isTagChange && activity.tagLabel && (
          <div className="mt-0.5">
            <TagChip label={activity.tagLabel} colorHex={activity.tagColorHex} />
          </div>
        )}
        {activity.followUpTagLabel && (
          <div className="mt-0.5">
            <TagChip label={activity.followUpTagLabel} colorHex={activity.followUpTagColorHex} />
          </div>
        )}
      </div>
      {activity.actionType === ActivityActionType.FILE_UPLOAD && activity.attachmentKey && (
        <div className="mt-1.5 ml-1">
          <FilePreviewCard
            fileKey={activity.attachmentKey}
            name={activity.attachmentName}
            sizeBytes={activity.attachmentSizeBytes}
            contentType={activity.attachmentContentType}
          />
        </div>
      )}
      {/* The email (headers + body) can be long, so it stays folded */}
      {activity.note && activity.actionType === ActivityActionType.EMAIL_SENT && (
        <details className={cn('mt-1.5 rounded-xl rounded-tl-sm px-3 py-2 ', bubbleStyle.bubble, bubbleStyle.border)}>
          <summary className="cursor-pointer text-xs text-neutral-500">Show email</summary>
          <p className="mt-2 text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{activity.note}</p>
        </details>
      )}
      {activity.note && activity.actionType !== ActivityActionType.CALL && activity.actionType !== ActivityActionType.EMAIL_SENT && (
        <div className={cn('mt-1.5 rounded-xl rounded-tl-sm px-3 py-2 ', bubbleStyle.bubble, bubbleStyle.border)}>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{activity.note}</p>
        </div>
      )}
    </div>
  )
}

// ─── Inline tag quick-create (shared by Activity composer and Record tag picker) ─

interface InlineTagCreateProps {
  onSubmit: (label: string, colorHex?: string) => void
  onCancel: () => void
  isLoading?: boolean
}

const PRESET_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899']

function InlineTagCreate({ onSubmit, onCancel, isLoading }: InlineTagCreateProps) {
  const [label, setLabel] = useState('')
  const [color, setColor] = useState<string | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const handleSubmit = () => {
    const trimmed = label.trim()
    if (!trimmed) return
    onSubmit(trimmed, color)
  }

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-2.5 space-y-2">
      <input
        ref={inputRef}
        value={label}
        onChange={e => setLabel(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); else if (e.key === 'Escape') onCancel() }}
        placeholder="Tag name…"
        maxLength={64}
        className="w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
      />
      <div className="flex items-center gap-1.5 flex-wrap">
        {PRESET_COLORS.map(c => (
          <button
            key={c}
            onClick={() => setColor(color === c ? undefined : c)}
            className={cn('h-5 w-5 rounded-full transition-all', color === c && 'ring-2 ring-offset-1 ring-neutral-400')}
            style={{ backgroundColor: c }}
          />
        ))}
        <span className="text-[10px] text-neutral-400 ml-1">color (optional)</span>
      </div>
      <div className="flex gap-1.5 justify-end">
        <button onClick={onCancel} className="h-6 px-2.5 rounded-lg border border-neutral-200 bg-white text-xs text-neutral-600 hover:bg-neutral-50">
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={!label.trim() || isLoading}
          className="h-6 px-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-xs font-semibold text-white flex items-center gap-1">
          {isLoading ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Create
        </button>
      </div>
    </div>
  )
}

// ─── Mini calendar popover (custom, app-rendered — replaces native <input type="date">
//      so the dropdown is always positioned on-screen instead of a native OS popup that
//      can render past the viewport edge) ──────────────────────────────────────────────
interface MiniCalendarProps {
  selected: string // yyyy-MM-dd, or '' for none
  onSelect: (dateStr: string) => void
  onClear: () => void
}

function MiniCalendar({ selected, onSelect, onClear }: MiniCalendarProps) {
  const selectedDate = selected ? parseDate(selected) : null
  const [viewDate, setViewDate] = useState(() => selectedDate ?? new Date())

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstWeekday = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today = new Date()

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

  const cells: (number | null)[] = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  return (
    <div className="rounded-xl border border-neutral-200 bg-white shadow-lg p-3 w-64">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))}
          className="h-6 w-6 rounded-md flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors">
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs font-semibold text-neutral-700">{monthLabel}</span>
        <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))}
          className="h-6 w-6 rounded-md flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="h-6 flex items-center justify-center text-[10px] font-semibold text-neutral-400">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />
          const cellDate = new Date(year, month, d)
          const isSelected = !!selectedDate && isSameDay(cellDate, selectedDate)
          const isToday = isSameDay(cellDate, today)
          return (
            <button key={i} type="button" onClick={() => onSelect(toISODateString(cellDate))}
              className={cn("h-7 w-7 rounded-lg text-xs flex items-center justify-center transition-colors",
                isSelected ? 'bg-primary-600 text-white font-semibold'
                  : isToday ? 'border border-primary-300 text-primary-700 font-semibold'
                  : 'text-neutral-700 hover:bg-neutral-100')}>
              {d}
            </button>
          )
        })}
      </div>
      {selected && (
        <button type="button" onClick={onClear}
          className="mt-2 text-[11px] font-semibold text-primary-600 hover:text-primary-700">
          Clear
        </button>
      )}
    </div>
  )
}


// ─── Main Page ────────────────────────────────────────────────────────────────

interface PendingEdit { fieldKey: string; fieldLabel: string; valueId: string | null; newValue: string }

export function RecordDetailPage() {
  const { recordGroupId, recordId } = useParams<{ recordGroupId: string; recordId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { record, isLoading, updateValueAsync, isUpdatingValue, delete: deleteRecord, isDeleting, clickButtonAsync } = useRecordDetail(recordId!)
  // Per-record permissions (honour record-group scoped grants and grants on this record).
  const recordActions = useAllowedActions('RECORD', recordId)
  const activityActions = useAllowedActions('RECORD_ACTIVITY', recordId)
  // Only load the timeline when it may be viewed — a 403 would surface as an error toast.
  const canViewActivity = activityActions.can(PERMISSION_KEYS.ACTIVITY_VIEW)
  const {
    activities, activitySessionId, addActivity, isAdding,
    uploadFileAsync, isUploadingFile,
    nextActivityAtReminder, nextActivityInstructions,
    followUpTagLabel, followUpTagColorHex,
    hasNextPage, fetchNextPage, isFetchingNextPage, isFetching: isFetchingActivity, isLoading: isLoadingActivity,
  } = useActivity(recordId!, { enabled: canViewActivity })

  // Calls tab next to Activity, for workspaces with calling and users who make or review calls.
  const { data: callingMe } = useCallingMe()
  const showCallsTab = !!callingMe?.featureEnabled && (callingMe.canMakeCalls || callingMe.canViewAll)
  const [rightTab, setRightTab] = useState<'activity' | 'calls'>('activity')
  // Not allowed to read the timeline: it is left out rather than shown locked, and the panel goes with it
  // unless the Calls tab is there to show.
  const activityBlocked = !activityActions.isLoading && !activityActions.isError && !canViewActivity
  const hideRightPanel = activityBlocked && !showCallsTab
  const callsTabActive = showCallsTab && (rightTab === 'calls' || activityBlocked)

  // Follow-up complete/reopen — resolves the pending follow-up shown in the
  // Activity panel header. Users land here after being redirected from the
  // Tasks page (follow-up items no longer open a completion modal there).
  const markFollowUpComplete = useMarkRecordFollowUpComplete()
  const reopenFollowUp = useReopenRecordFollowUp()
  const [isResolvingFollowUp, setIsResolvingFollowUp] = useState(false)
  const hasPendingFollowUp = !!nextActivityAtReminder && nextActivityAtReminder !== '1970-01-01'

  const handleCompleteFollowUp = useCallback(async () => {
    if (!recordId) return
    setIsResolvingFollowUp(true)
    try {
      await markFollowUpComplete.mutateAsync({
        recordId,
        data: { recordActivitySessionId: activitySessionId ?? null, note: null },
      })
      toast.success('Follow-up completed')
    } catch {
      toast.error('Failed to complete follow-up')
    } finally {
      setIsResolvingFollowUp(false)
    }
  }, [recordId, activitySessionId, markFollowUpComplete])

  const [showReopenFollowUp, setShowReopenFollowUp] = useState(false)
  const [reopenDate, setReopenDate] = useState('')
  const [reopenInstructions, setReopenInstructions] = useState('')

  const handleReopenFollowUp = useCallback(async () => {
    if (!recordId || !reopenDate) return
    setIsResolvingFollowUp(true)
    try {
      await reopenFollowUp.mutateAsync({
        recordId,
        data: {
          recordActivitySessionId: activitySessionId ?? null,
          note: null,
          nextActivityAt: reopenDate,
          nextActivityInstructions: reopenInstructions.trim() || undefined,
        },
      })
      toast.success('Follow-up reopened')
      setShowReopenFollowUp(false)
      setReopenDate('')
      setReopenInstructions('')
    } catch {
      toast.error('Failed to reopen follow-up')
    } finally {
      setIsResolvingFollowUp(false)
    }
  }, [recordId, activitySessionId, reopenFollowUp, reopenDate, reopenInstructions])

  // Phase 3 — field-key visibility toggle
  const [showFieldKeys, setShowFieldKeys] = useLocalStorageState('recordDetail:showFieldKeys', false)

  // Phase 4 — activity tags
  const { tags: activityTags, createTagAsync: createActivityTagAsync, isCreating: isCreatingActivityTag } =
    useTags(recordGroupId, 'ACTIVITY')
  const [selectedTagId, setSelectedTagId]             = useState<string | undefined>(undefined)
  const [instructions, setInstructions]               = useState('')
  const [showActivityTagPicker, setShowActivityTagPicker] = useState(false)
  const [showActivityTagCreate, setShowActivityTagCreate] = useState(false)

  // Phase 6 — follow-up tags
  const { tags: followUpTags, createTagAsync: createFollowUpTagAsync, isCreating: isCreatingFollowUpTag } =
    useTags(recordGroupId, 'FOLLOWUP')
  const [selectedFollowUpTagId, setSelectedFollowUpTagId] = useState<string | undefined>(undefined)
  const [showFollowUpTagPicker, setShowFollowUpTagPicker] = useState(false)
  const [showFollowUpTagCreate, setShowFollowUpTagCreate] = useState(false)
  const [showDatePicker, setShowDatePicker]             = useState(false)

  // Phase 5 — record tags
  const { tags: recordTagOptions, createTagAsync: createRecordTagAsync, isCreating: isCreatingRecordTag } =
    useTags(recordGroupId, 'RECORD')
  const { recordTags, assignTag, assignTagAsync, unassignTag } = useRecordTags(recordId)
  // Categories group the pickers; creating tags inline needs tag management rights on the group.
  const { categories: recordTagCategories } = useTagCategories(recordGroupId, 'RECORD')
  const { categories: activityTagCategories } = useTagCategories(recordGroupId, 'ACTIVITY')
  const { categories: followUpTagCategories } = useTagCategories(recordGroupId, 'FOLLOWUP')
  const groupActions = useAllowedActions('RECORD_GROUP', recordGroupId)
  const canManageTags = groupActions.can(PERMISSION_KEYS.TAG_MANAGE)
  const [showRecordTagPicker, setShowRecordTagPicker] = useState(false)
  const [showRecordTagCreate, setShowRecordTagCreate] = useState(false)

  // Field editing state
  const [pendingEdits, setPendingEdits] = useState<Record<string, PendingEdit>>({})
  const [saveErrors, setSaveErrors]     = useState<Record<string, string>>({})
  const [isSavingAll, setIsSavingAll]   = useState(false)
  // Fields whose current value fails local validation: they block "Update Data" until fixed.
  const [invalidFields, setInvalidFields] = useState<Record<string, string>>({})
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    Object.keys(pendingEdits).length > 0 && currentLocation.pathname !== nextLocation.pathname)

  // Composer state
  const [note, setNote]               = useState('')
  const [reminderDate, setReminderDate] = useState('')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [fileUploadOpen, setFileUploadOpen] = useState(false)
  const { data: fileConfig } = useFileUploadConfig()
  const feedBottomRef                 = useRef<HTMLDivElement>(null)

  const canEdit    = recordActions.can(PERMISSION_KEYS.RECORD_UPDATE)
  const canAddNote = activityActions.can(PERMISSION_KEYS.ACTIVITY_ADD)
  // Tagging has its own permissions; restricted tags (canUse === false) are left out of pickers.
  const canTagRecord   = recordActions.can(PERMISSION_KEYS.RECORD_TAG_ASSIGN)
  const canTagActivity = activityActions.can(PERMISSION_KEYS.ACTIVITY_TAG_ASSIGN)
  const canTagFollowUp = activityActions.can(PERMISSION_KEYS.FOLLOWUP_TAG_ASSIGN)
  const isUsableTag = (tagId: string) => recordTagOptions.find((t) => t.id === tagId)?.canUse !== false
  const canDelete  = recordActions.can(PERMISSION_KEYS.RECORD_DELETE)
  const dirtyCount = Object.keys(pendingEdits).length

  // Scroll activity feed to bottom on new activity
  useEffect(() => {
    if (isFetchingNextPage) return
    feedBottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activities.length, isFetchingNextPage])

  const handleFieldChange = (
    fieldId: string, fieldKey: string, fieldLabel: string, valueId: string | null,
    savedValue: string, newValue: string, error?: string | null
  ) => {
    setInvalidFields(prev => {
      if (!error) { if (!(fieldId in prev)) return prev; const n = { ...prev }; delete n[fieldId]; return n }
      return { ...prev, [fieldId]: error }
    })
    setSaveErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n })
    if (newValue === savedValue) {
      setPendingEdits(prev => { const n = { ...prev }; delete n[fieldId]; return n })
    } else {
      setPendingEdits(prev => ({ ...prev, [fieldId]: { fieldKey, fieldLabel, valueId, newValue } }))
    }
  }

  const handleSaveAll = async () => {
    setIsSavingAll(true)
    const entries = Object.entries(pendingEdits)
    const newSaveErrors: Record<string, string> = {}
    const savedIds: string[] = []
    const failedLabels: Array<{ label: string; reason: string }> = []
    for (const [fieldId, edit] of entries) {
      try {
        await updateValueAsync({
          recordGroupId: recordGroupId!,
          data: edit.valueId
            ? { fieldKey: edit.fieldKey, fieldLabel: edit.fieldLabel, valueId: edit.valueId, value: edit.newValue }
            : { fieldKey: edit.fieldKey, fieldLabel: edit.fieldLabel, recordId: recordId!, formFieldId: fieldId, value: edit.newValue },
        })
        savedIds.push(fieldId)
      } catch (err) {
        const apiErr = parseApiError(err)
        const reason = apiErr.detail || 'Update failed'
        newSaveErrors[fieldId] = reason
        failedLabels.push({ label: edit.fieldLabel, reason })
      }
    }
    setPendingEdits(prev => { const n = { ...prev }; savedIds.forEach(id => delete n[id]); return n })
    setInvalidFields({})
    setSaveErrors(prev => ({ ...prev, ...newSaveErrors }))
    setIsSavingAll(false)
    if (savedIds.length > 0) queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    const ok = savedIds.length, fail = failedLabels.length
    if (fail === 0) {
      toast.success(`${ok} field${ok !== 1 ? 's' : ''} updated successfully`)
    } else if (ok === 0) {
      toast.error('All updates failed', { description: failedLabels.map(f => `• ${f.label}: ${f.reason}`).join('\n'), duration: 8000 })
    } else {
      toast.success(`${ok} field${ok !== 1 ? 's' : ''} updated`)
      toast.error(`${fail} field${fail !== 1 ? 's' : ''} failed`, { description: failedLabels.map(f => `• ${f.label}: ${f.reason}`).join('\n'), duration: 8000 })
    }
  }

  // Phase 4 — handleAddNote with tag + instructions + follow-up tag support
  const handleAddNote = () => {
    if (!note.trim()) { toast.error('Note cannot be empty'); return }
    if (!activitySessionId) { toast.error('Activity session unavailable — try refreshing'); return }
    const payload: AddActivityRequest = { note: note.trim(), recordActivitySessionId: activitySessionId }
    if (reminderDate) payload.nextActivityAt = reminderDate
    if (reminderDate && instructions.trim()) payload.nextActivityInstructions = instructions.trim()
    if (selectedTagId) payload.tagId = selectedTagId
    if (reminderDate && selectedFollowUpTagId) payload.followUpTagId = selectedFollowUpTagId
    addActivity(payload, {
      onSuccess: () => {
        toast.success('Note added')
        setNote(''); setReminderDate(''); setInstructions(''); setSelectedTagId(undefined); setSelectedFollowUpTagId(undefined)
      },
      onError: () => toast.error('Failed to add note'),
    })
  }

  const handleDeleteRecord = () => {
    deleteRecord(recordId!, {
      onSuccess: () => {
        toast.success('Record moved to trash')
        setShowDeleteDialog(false)
        navigate(`/record-groups/${recordGroupId}`)
      },
      onError: (error) => {
        toast.error(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`)
      },
    })
  }

  const recordTitle = record?.title || record?.fieldsAndValues?.[0]?.value || 'Record Details'
  const hasSaveErrors = Object.keys(saveErrors).length > 0

  if (isLoading) {
    return (
      <div className="h-full p-5 flex flex-col gap-5">
        <div className="flex gap-2 items-center">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-32 rounded-full" />
        </div>
        <div className="flex-1 grid gap-5 lg:grid-cols-[3fr_2fr]">
          <Skeleton className="rounded-2xl" />
          <Skeleton className="rounded-2xl" />
        </div>
      </div>
    )
  }

  if (!record) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center space-y-3">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-neutral-100 flex items-center justify-center text-xl">📋</div>
          <p className="font-semibold text-neutral-600">Record not found</p>
          <button onClick={() => navigate(`/record-groups/${recordGroupId}`)} className="text-sm text-primary-600 hover:underline">
            Back to records
          </button>
        </div>
      </div>
    )
  }

  // "Show when…" rules follow what is on screen, unsaved edits included
  const answers = valuesByKey(
    record.fieldsAndValues.map(({ field }) => field),
    (f) => pendingEdits[f.id]?.newValue ?? record.fieldsAndValues.find(({ field }) => field.id === f.id)?.value
  )

  return (
    <div className="flex flex-col -mx-4 -my-4 lg:-mx-6 lg:-my-6 h-full overflow-y-auto px-5 py-4 gap-4">

      <RecordGroupBreadcrumbs recordGroupId={recordGroupId!} trailing={[{ label: recordTitle }]} className="shrink-0" />

      {/* ── Header bar ── */}
      <div className="flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(`/record-groups/${recordGroupId}`)}
            className="h-8 w-8 rounded-xl border border-neutral-200 bg-white flex items-center justify-center hover:bg-neutral-50 transition-colors shrink-0">
            <ArrowLeft className="h-4 w-4 text-neutral-600" />
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-bold text-neutral-900 truncate leading-tight">{recordTitle}</h1>
            {record.updatedAt && (
              <p className="text-[10px] text-neutral-400">Updated {formatDateTime(record.updatedAt)}</p>
            )}
          </div>
        </div>
        {(dirtyCount > 0 || hasSaveErrors) && (
          <div className="flex items-center gap-2 shrink-0">
            {hasSaveErrors && dirtyCount === 0 && (
              <span className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                <AlertCircle className="h-3 w-3" />
                {Object.keys(saveErrors).length} error{Object.keys(saveErrors).length !== 1 ? 's' : ''}
              </span>
            )}
            {dirtyCount > 0 && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                {dirtyCount} unsaved
              </span>
            )}
            <button onClick={() => { setPendingEdits({}); setSaveErrors({}); setInvalidFields({}) }}
              className="h-8 px-3 rounded-xl border border-neutral-200 bg-white text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors flex items-center gap-1.5">
              <X className="h-3.5 w-3.5" /> Discard
            </button>
            {dirtyCount > 0 && (
              <button onClick={handleSaveAll} disabled={isSavingAll || isUpdatingValue || Object.keys(invalidFields).length > 0}
                title={Object.keys(invalidFields).length > 0 ? 'Fix the highlighted fields first' : undefined}
                className="h-8 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-xs font-semibold text-white flex items-center gap-1.5 shadow-sm transition-colors">
                {isSavingAll ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Update Data
              </button>
            )}
          </div>
        )}
        {!dirtyCount && !hasSaveErrors && canDelete && (
          <button onClick={() => setShowDeleteDialog(true)} disabled={isDeleting}
            className="h-8 px-3 rounded-xl border border-rose-300 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-xs font-medium text-rose-700 flex items-center gap-1.5 transition-colors shrink-0">
            {isDeleting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Move to Trash
          </button>
        )}
      </div>

      {/* ── 3:2 two-column body ── */}
      <div className={cn('flex-1 min-h-0 grid gap-4', !hideRightPanel && 'lg:grid-cols-[3fr_2fr]')}>

        {/* LEFT — Record Fields */}
        <div className="flex flex-col min-h-0 bg-white rounded-2xl border border-neutral-200/80 shadow-sm overflow-hidden">
          {/* Panel header with Phase 3 Eye toggle */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100 shrink-0">
            <div>
              <h2 className="text-sm font-semibold text-neutral-800">Record Data</h2>
              <p className="text-[10px] text-neutral-400 mt-px">{canEdit ? 'Click any field to edit' : 'View only'}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFieldKeys(!showFieldKeys)}
                title={showFieldKeys ? 'Hide internal field keys' : 'Show internal field keys — used in automation templates, e.g. {{fieldKey}}'}
                className={cn('h-6 w-6 rounded-lg flex items-center justify-center transition-colors border',
                  showFieldKeys
                    ? 'bg-primary-100 text-primary-700 border-primary-200'
                    : 'bg-neutral-100 text-neutral-400 hover:text-neutral-600 border-transparent')}>
                {showFieldKeys ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              </button>
              <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-100 rounded-full px-2.5 py-1">
                {record.fieldsAndValues.filter(({ field }) => !isButtonField(field) && !isLayoutField(field)).length} fields
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <CreatorCard sourceType={record.sourceType} creatorName={record.creatorName} creatorEmail={record.creatorEmail} />

            {/* Phase 5 — Record tag chip row */}
            <div className="mx-0 mb-3">
              <div className="flex items-center flex-wrap gap-1.5">
                {recordTags.map(tag => (
                  <TagChip key={tag.tagId} label={tag.label} colorHex={tag.colorHex}
                    onRemove={canTagRecord && isUsableTag(tag.tagId) ? () => unassignTag(tag.tagId) : undefined} />
                ))}
                {canTagRecord && (
                  <button onClick={() => { setShowRecordTagPicker(!showRecordTagPicker); setShowRecordTagCreate(false) }}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border border-dashed border-neutral-300 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors">
                    <Plus className="h-3 w-3" /> tag
                  </button>
                )}
              </div>
              {showRecordTagPicker && (
                <div className="mt-2 rounded-xl border border-neutral-200 bg-white shadow-lg p-2 space-y-1 max-h-48 overflow-y-auto">
                  <CategorizedTagOptions
                    tags={recordTagOptions.filter(t => t.canUse !== false && !recordTags.some(rt => rt.tagId === t.id))}
                    categories={recordTagCategories}
                    showSingleSelectHint
                    onPick={(tag) => { assignTag(tag.id); setShowRecordTagPicker(false) }}
                    renderTag={(tag) => <TagChip label={tag.label} colorHex={tag.colorHex} />}
                  />
                  {!canManageTags ? null : showRecordTagCreate ? (
                    <InlineTagCreate
                      onSubmit={async (label, colorHex) => {
                        try {
                          const newTag = await createRecordTagAsync({ label, colorHex })
                          await assignTagAsync(newTag.id)
                          setShowRecordTagCreate(false); setShowRecordTagPicker(false)
                        } catch (err) {
                          toast.error(parseApiError(err).detail || 'Failed to create tag')
                        }
                      }}
                      onCancel={() => setShowRecordTagCreate(false)}
                      isLoading={isCreatingRecordTag}
                    />
                  ) : (
                    <button onClick={() => setShowRecordTagCreate(true)}
                      className="w-full text-left text-[11px] text-primary-600 font-semibold px-2 py-1 hover:bg-primary-50 rounded-lg flex items-center gap-1.5">
                      <Plus className="h-3 w-3" /> New tag…
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Fields: a bento grid. Cells keep their own height (items-start), wide ones take the whole
                row, and dense packing lets later short fields fill the gaps. */}
            <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-3 items-start',
              // dense packing would pull fields across a heading or line break the designer placed
              !record.fieldsAndValues.some(({ field }) => isLayoutField(field)) && 'grid-flow-row-dense')}>
              {record.fieldsAndValues.map(({ field, valueId, value, editable, lastClick }) => {
                const pending = pendingEdits[field.id]
                if (!isFieldVisible(field, answers)) return null
                if (isLayoutField(field)) {
                  return (
                    <div key={field.id} className="sm:col-span-2">
                      <LayoutFieldView field={field} />
                    </div>
                  )
                }
                if (isButtonField(field)) {
                  return (
                    <RecordButtonField
                      key={field.id}
                      field={field}
                      lastClick={lastClick}
                      canClick={canEdit && editable}
                      viewOnly={canEdit && !editable}
                      unsaved={dirtyCount > 0}
                      click={clickButtonAsync}
                    />
                  )
                }
                return (
                  <FieldCell
                    key={field.id}
                    fieldId={field.id}
                    fieldKey={field.key}
                    fieldLabel={field.label}
                    fieldType={field.type}
                    required={field.required}
                    validations={field.validations as Record<string, unknown> | null}
                    options={field.options as Record<string, unknown> | null}
                    value={value}
                    pendingValue={pending?.newValue}
                    saveError={saveErrors[field.id]}
                    canEdit={canEdit && editable}
                    viewOnly={canEdit && !editable}
                    showFieldKeys={showFieldKeys}
                    recordGroupId={recordGroupId!}
                    recordId={recordId!}
                    valuesByKey={answers}
                    onChange={(newVal, err) => handleFieldChange(field.id, field.key, field.label, valueId, value, newVal, err)}
                  />
                )
              })}
            </div>
            {canEdit && (
              <p className="text-[10px] text-neutral-400 mt-4 text-center">
                Changes save together with "Update Data" · Escape restores a field's saved value
              </p>
            )}
          </div>
        </div>

        {/* RIGHT — Activity */}
        {!hideRightPanel && (
        <div className="flex flex-col min-h-0 bg-white rounded-2xl border border-neutral-200/80 shadow-sm overflow-hidden">
          {/* Activity panel header — Phase 4: shows nextActivityInstructions next to date */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 border-b border-neutral-100 shrink-0">
            <div className="h-7 w-7 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
              <MessageSquare className="h-3.5 w-3.5 text-primary-600" />
            </div>
            <div className="flex-1 min-w-fit">
              {showCallsTab && !activityBlocked ? (
                <div className="flex items-center gap-1" role="tablist" aria-label="Record history">
                  {(['activity', 'calls'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={rightTab === tab}
                      onClick={() => setRightTab(tab)}
                      className={cn(
                        'rounded-lg px-2 py-0.5 text-sm font-semibold transition-colors',
                        rightTab === tab ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-400 hover:text-neutral-700'
                      )}
                    >
                      {tab === 'activity' ? 'Activity' : 'Calls'}
                    </button>
                  ))}
                </div>
              ) : (
                <h2 className="text-sm font-semibold text-neutral-800">{activityBlocked ? 'Calls' : 'Activity'}</h2>
              )}
              {!callsTabActive && <p className="text-[10px] text-neutral-400">{activities.length} events</p>}
            </div>
            {nextActivityAtReminder && nextActivityAtReminder !== '1970-01-01' && (
              <div className="ml-auto text-[10px] font-semibold text-primary-700 flex items-center gap-1 min-w-0">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                <span className="shrink-0">{formatDate(nextActivityAtReminder)}</span>
                {nextActivityInstructions && (
                  <span className="ml-1 text-[10px] font-medium text-primary-800 bg-primary-50 border border-primary-200 rounded-md px-1.5 py-0.5 truncate max-w-[180px]"
                    title={nextActivityInstructions}>
                    {nextActivityInstructions}
                  </span>
                )}
                {followUpTagLabel && (
                  <span
                    className="ml-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold border shrink-0"
                    style={followUpTagColorHex ? {
                      backgroundColor: followUpTagColorHex + '22',
                      borderColor: followUpTagColorHex + '55',
                      color: followUpTagColorHex,
                    } : undefined}
                  >
                    {followUpTagColorHex && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: followUpTagColorHex }} />}
                    {followUpTagLabel}
                  </span>
                )}
                {canAddNote && (
                  <button
                    onClick={handleCompleteFollowUp}
                    disabled={isResolvingFollowUp}
                    className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-primary-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-primary-700 shrink-0 hover:bg-primary-50 transition-colors disabled:opacity-50"
                  >
                    {isResolvingFollowUp ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />}
                    Mark complete
                  </button>
                )}
              </div>
            )}
            {!hasPendingFollowUp && canAddNote && (
              <div className="ml-auto relative shrink-0">
                <button
                  onClick={() => setShowReopenFollowUp((v) => !v)}
                  className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-500 hover:border-primary-300 hover:text-primary-700 transition-colors"
                >
                  <Undo2 className="h-3 w-3" />
                  Reopen follow-up
                </button>
                {showReopenFollowUp && (
                  <div className="absolute right-0 top-full mt-1.5 w-64 rounded-xl border border-neutral-200 bg-white shadow-lg p-3 space-y-2 z-50">
                    <div className="space-y-1">
                      <label className="text-[10px] font-semibold text-neutral-500">Next activity date</label>
                      <DateInput value={reopenDate} onChange={setReopenDate} className="h-8 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-semibold text-neutral-500">Instructions (optional)</label>
                      <textarea
                        value={reopenInstructions}
                        onChange={(e) => setReopenInstructions(e.target.value)}
                        maxLength={500}
                        placeholder="What should happen next?"
                        className="w-full min-h-[50px] rounded-lg border border-neutral-200 px-2 py-1 text-xs text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                    </div>
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => { setShowReopenFollowUp(false); setReopenDate(''); setReopenInstructions('') }}
                        className="h-6 px-2.5 rounded-lg border border-neutral-200 bg-white text-[11px] text-neutral-600 hover:bg-neutral-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleReopenFollowUp}
                        disabled={!reopenDate || isResolvingFollowUp}
                        className="h-6 px-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-[11px] font-semibold text-white flex items-center gap-1"
                      >
                        {isResolvingFollowUp ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                        Reopen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Activity feed */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 overscroll-contain relative">
            {callsTabActive ? <RecordCallsPanel recordId={recordId!} /> : (<>
            {hasNextPage && (
              <div className="flex justify-center">
                <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
                  className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-neutral-400 hover:text-primary-600 transition-colors py-1">
                  {isFetchingNextPage ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ChevronUp className="h-3 w-3" />}
                  Load earlier
                </button>
              </div>
            )}
            {isLoadingActivity ? (
              <div className="space-y-3 pt-1">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
            ) : activities.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-6">
                <div className="h-12 w-12 rounded-2xl bg-neutral-100 flex items-center justify-center mb-3">
                  <MessageSquare className="h-5 w-5 text-neutral-300" />
                </div>
                <p className="text-sm font-semibold text-neutral-400">No activity yet</p>
                <p className="text-[11px] text-neutral-300 mt-1">Notes and changes will appear here</p>
              </div>
            ) : (
              <div className="relative pl-0">
                <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-neutral-200"></div>
                <div className="space-y-0">
                {activities.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-6">
                    <div className="h-12 w-12 rounded-2xl bg-neutral-100 flex items-center justify-center mb-3">
                      <MessageSquare className="h-5 w-5 text-neutral-300" />
                    </div>
                    <p className="text-sm font-semibold text-neutral-400">No activity yet</p>
                    <p className="text-[11px] text-neutral-300 mt-1">Notes and changes will appear here</p>
                  </div>
                ) : (
                  activities.slice().reverse().map((activity, i) => (
                    <ActivityBubble key={activity.id ?? `${activity.timestamp}-${i}`} activity={activity} />
                  ))
                )}
                </div>
              </div>
            )}
            </>)}
            <div ref={feedBottomRef} />
          </div>

          {/* ── Note composer (Phase 4: activity tag + instructions; Phase 6: follow-up tag) ── */}
          {canAddNote && !callsTabActive && (
            <div className="shrink-0 border-t border-neutral-100 bg-neutral-50/60 space-y-2">
              {/* ── Upper capsule: follow-up date + follow-up tag on right ── */}
              {reminderDate && (
                <div className="px-3 pt-3 pb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Follow-up date pill */}
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-primary-700 bg-primary-50 border border-primary-100 rounded-lg px-2.5 py-1.5 shrink-0">
                      <Calendar className="h-2.5 w-2.5" />
                      Follow-up: {formatDate(reminderDate)}
                      <button onClick={() => { setReminderDate(''); setInstructions(''); setSelectedFollowUpTagId(undefined) }} className="ml-1 text-neutral-400 hover:text-neutral-600">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>

                    {/* Follow-up tag selector - right side of date */}
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                      {selectedFollowUpTagId ? (
                        (() => {
                          const tag = followUpTags.find(t => t.id === selectedFollowUpTagId)
                          return tag ? <TagChip label={tag.label} colorHex={tag.colorHex} onRemove={() => setSelectedFollowUpTagId(undefined)} /> : null
                        })()
                      ) : canTagFollowUp && (
                        <button onClick={() => { setShowFollowUpTagPicker(!showFollowUpTagPicker); setShowFollowUpTagCreate(false) }}
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border border-dashed border-neutral-300 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors shrink-0">
                          <TagIcon className="h-3 w-3" /> Follow-up tag
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Follow-up tag picker dropdown */}
                  {showFollowUpTagPicker && (
                    <div className="mt-1.5 rounded-xl border border-neutral-200 bg-white shadow-lg p-2 space-y-1 max-h-40 overflow-y-auto">
                      <CategorizedTagOptions
                        tags={followUpTags.filter(t => t.canUse !== false)}
                        categories={followUpTagCategories}
                        onPick={(tag) => { setSelectedFollowUpTagId(tag.id); setShowFollowUpTagPicker(false) }}
                        renderTag={(tag) => <TagChip label={tag.label} colorHex={tag.colorHex} />}
                      />
                      {!canManageTags ? null : showFollowUpTagCreate ? (
                        <InlineTagCreate
                          onSubmit={async (label, colorHex) => {
                            try {
                              const newTag = await createFollowUpTagAsync({ label, colorHex })
                              setSelectedFollowUpTagId(newTag.id)
                              setShowFollowUpTagCreate(false); setShowFollowUpTagPicker(false)
                            } catch (err) {
                              toast.error(parseApiError(err).detail || 'Failed to create tag')
                            }
                          }}
                          onCancel={() => setShowFollowUpTagCreate(false)}
                          isLoading={isCreatingFollowUpTag}
                        />
                      ) : (
                        <button onClick={() => setShowFollowUpTagCreate(true)}
                          className="w-full text-left text-[11px] text-primary-600 font-semibold px-2 py-1 hover:bg-primary-50 rounded-lg flex items-center gap-1.5">
                          <Plus className="h-3 w-3" /> New tag…
                        </button>
                      )}
                    </div>
                  )}

                  {/* Instructions input (full width) */}
                  <input type="text" value={instructions} onChange={e => setInstructions(e.target.value)}
                    placeholder="Next activity instructions (optional)…"
                    className="mt-1.5 w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                </div>
              )}

              {/* ── Lower capsule: textarea + activity tag + date picker + send on right ── */}
              {/* ── Lower capsule: textarea + activity tag + date picker + send ── */}
              <div className="p-3">
                {/* Textarea + right side: activity tag + date picker + send */}
                <div className="flex items-end gap-2">
                  <textarea value={note} onChange={e => setNote(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { if (e.ctrlKey || e.metaKey) handleAddNote() } }}
                    placeholder="Write a note... (Ctrl+Enter to send)" rows={2}
                    className="flex-1 resize-none rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all" />

                  {/* Right side: activity tag (top row) + date picker/send (bottom row) */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {/* Activity tag selector - own row above date/send */}
                    <div className="relative flex items-center gap-1">
                      {selectedTagId ? (
                        (() => {
                          const tag = activityTags.find(t => t.id === selectedTagId)
                          return tag ? <TagChip label={tag.label} colorHex={tag.colorHex} onRemove={() => setSelectedTagId(undefined)} /> : null
                        })()
                      ) : canTagActivity && (
                        <button onClick={() => { setShowActivityTagPicker(!showActivityTagPicker); setShowActivityTagCreate(false) }}
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border border-dashed border-neutral-300 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors shrink-0">
                          <TagIcon className="h-3 w-7" /> Tag
                        </button>
                      )}

                      {/* Activity tag picker dropdown */}
                      {showActivityTagPicker && (
                        <div className="absolute bottom-full mb-1 right-0 rounded-xl border border-neutral-200 bg-white shadow-lg p-2 space-y-1 max-h-40 overflow-y-auto z-50 w-48">
                          <CategorizedTagOptions
                            tags={activityTags.filter(t => t.canUse !== false)}
                            categories={activityTagCategories}
                            onPick={(tag) => { setSelectedTagId(tag.id); setShowActivityTagPicker(false) }}
                            renderTag={(tag) => <TagChip label={tag.label} colorHex={tag.colorHex} />}
                          />
                          {!canManageTags ? null : showActivityTagCreate ? (
                            <InlineTagCreate
                              onSubmit={async (label, colorHex) => {
                                try {
                                  const newTag = await createActivityTagAsync({ label, colorHex })
                                  setSelectedTagId(newTag.id)
                                  setShowActivityTagCreate(false); setShowActivityTagPicker(false)
                                } catch (err) {
                                  toast.error(parseApiError(err).detail || 'Failed to create tag')
                                }
                              }}
                              onCancel={() => setShowActivityTagCreate(false)}
                              isLoading={isCreatingActivityTag}
                            />
                          ) : (
                            <button onClick={() => setShowActivityTagCreate(true)}
                              className="w-full text-left text-[11px] text-primary-600 font-semibold px-2 py-1 hover:bg-primary-50 rounded-lg flex items-center gap-1.5">
                              <Plus className="h-3 w-3" /> New tag...
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Bottom row: attach file + date picker + send */}
                    <div className="flex items-center gap-1.5">
                      {/* Attach file (FILE_UPLOAD activity) */}
                      <button
                        type="button"
                        onClick={() => setFileUploadOpen(true)}
                        title="Upload a file"
                        disabled={isUploadingFile || !activitySessionId}
                        className="h-8 w-8 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-100 flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40"
                      >
                        <Paperclip className={cn('h-3.5 w-3.5', isUploadingFile ? 'text-neutral-300' : 'text-neutral-500')} />
                      </button>

                      {/* Date picker (custom popover — replaces native <input type="date> that could render off-screen) */}
                      <div className="relative">
                        <button type="button" onClick={() => setShowDatePicker(prev => !prev)}
                          title={reminderDate ? 'Follow-up date set - click to change' : 'Set follow-up date (optional)'}
                          className={cn("h-8 w-8 rounded-lg border flex items-center justify-center cursor-pointer transition-colors",
                            reminderDate ? 'border-primary-200 bg-primary-50' : 'border-neutral-200 bg-white hover:bg-neutral-100')}>
                          <Calendar className={cn("h-3.5 w-3.5", reminderDate ? 'text-primary-600' : 'text-neutral-500')} />
                        </button>
                        {showDatePicker && (
                          <div className="absolute bottom-full mb-1 right-0 z-50">
                            <MiniCalendar
                              selected={reminderDate}
                              onSelect={(dateStr) => { setReminderDate(dateStr); setShowDatePicker(false) }}
                              onClear={() => { setReminderDate(''); setShowDatePicker(false) }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Send button */}
                      <button onClick={handleAddNote} disabled={isAdding || !note.trim()}
                        title={!activitySessionId ? 'Session unavailable' : 'Send note'}
                        className="h-8 w-8 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-40 flex items-center justify-center transition-colors">
                        {isAdding ? <RefreshCw className="h-3.5 w-3.5 text-white animate-spin" /> : <Send className="h-3.5 w-3.5 text-white" />}
                      </button>
                    </div>
                  </div>
                </div>

                {!activitySessionId && !isLoadingActivity && !isFetchingActivity && activities.length === 0 && (
                  <p className="mt-2 text-[10px] font-medium text-amber-600">⚠ Session unavailable - refresh the page</p>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      {/* File upload modal for FILE_UPLOAD activities */}
      {fileUploadOpen && (
        <FileUploadModal
          open={fileUploadOpen}
          onOpenChange={setFileUploadOpen}
          title="Upload a document"
          description="The file is attached to this record's activity timeline and counted against the workspace storage quota."
          upload={async (file, options) => {
            await uploadFileAsync({
              file,
              recordActivitySessionId: activitySessionId ?? '',
              note: note.trim() || undefined,
              onProgress: options.onProgress,
              signal: options.signal,
              contentEncoding: options.contentEncoding,
              originalSize: options.originalSize,
            })
          }}
          onUploaded={() => {
            toast.success('File uploaded')
            setNote('')
          }}
          rules={{ maxFileSizeBytes: fileConfig?.maxFileSizeBytes ?? null }}
          catalog={fileConfig?.allowedTypes ?? []}
        />
      )}

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => { if (!open && blocker.state === 'blocked') blocker.reset() }}
        title="Discard unsaved changes?"
        description="You have unsaved field edits on this record. Leave and discard them?"
        cancelLabel="Stay"
        confirmLabel="Discard changes"
        destructive
        onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed() }}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Move Record to Trash?</DialogTitle>
              <DialogDescription>
                This record will be moved to the trash. You can restore it from the Trash page within 30 days.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <button onClick={() => setShowDeleteDialog(false)}
                className="h-8 px-4 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteRecord} disabled={isDeleting}
                className="h-8 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-sm font-medium text-white flex items-center gap-1.5 transition-colors">
                {isDeleting ? <RefreshCcw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Move to Trash
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
